<template>
  <div class="polynomial-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 面积模型主体 -->
           <g transform="translate(250, 150)">
              <!-- x^2 蓝色区域 -->
              <rect x="0" y="0" :width="xSize" :height="xSize" 
                    fill="rgba(59, 130, 246, 0.5)" stroke="#3b82f6" stroke-width="3"/>
              <text :x="xSize/2" :y="xSize/2 + 10" fill="#fff" text-anchor="middle" font-size="32" font-weight="950">x²</text>
              
              <!-- ax 红色区域 -->
              <rect :x="xSize" y="0" :width="aSize" :height="xSize" 
                    fill="rgba(239, 68, 68, 0.5)" stroke="#ef4444" stroke-width="3"/>
              <text :x="xSize + aSize/2" :y="xSize/2 + 8" fill="#fff" text-anchor="middle" font-size="24" font-weight="900" v-if="aSize > 20">ax</text>

              <!-- bx 绿色区域 -->
              <rect x="0" :y="xSize" :width="xSize" :height="bSize" 
                    fill="rgba(16, 185, 129, 0.5)" stroke="#10b981" stroke-width="3"/>
              <text :x="xSize/2" :y="xSize + bSize/2 + 8" fill="#fff" text-anchor="middle" font-size="24" font-weight="900" v-if="bSize > 20">bx</text>

              <!-- ab 橙色区域 -->
              <rect :x="xSize" :y="xSize" :width="aSize" :height="bSize" 
                    fill="rgba(245, 158, 11, 0.5)" stroke="#f59e0b" stroke-width="3"/>
              <text :x="xSize + aSize/2" :y="xSize + bSize/2 + 8" fill="#fff" text-anchor="middle" font-size="24" font-weight="900" v-if="aSize > 20 && bSize > 20">ab</text>
              
              <!-- 标注尺寸 -->
              <text :x="xSize/2" y="-15" fill="#60a5fa" text-anchor="middle" font-size="22" font-weight="900">x</text>
              <text :x="xSize + aSize/2" y="-15" fill="#f87171" text-anchor="middle" font-size="22" font-weight="900">+ a</text>
              <text x="-20" :y="xSize/2 + 8" fill="#60a5fa" text-anchor="end" font-size="22" font-weight="900">x</text>
              <text x="-20" :y="xSize + bSize/2 + 8" fill="#34d399" text-anchor="end" font-size="22" font-weight="900">+ b</text>
           </g>

           <text x="50" y="550" fill="rgba(255,255,255,0.3)" font-size="16" font-weight="bold">{{ mode === 'factor' ? '多项式因式分解几何模拟' : '多项式乘法几何面积模拟器' }}</text>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>{{ mode === 'factor' ? '因式分解结果' : '展开式左侧' }}</label>
              <span class="val">(x + {{ Math.round(aVal) }})(x + {{ Math.round(bVal) }})</span>
           </div>
           <div class="hud-item cyan">
              <label>{{ mode === 'factor' ? '多项式总面积' : '总面积 S' }}</label>
              <span class="val">{{ Math.round((xUnits + aVal)*(xUnits + bVal)) }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>参数 a (展开长度) : {{ aVal }}</label>
                 <el-slider v-model="aVal" :min="0" :max="100" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>参数 b (展开长度) : {{ bVal }}</label>
                 <el-slider v-model="bVal" :min="0" :max="100" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>参数 x (基准大小) : {{ xUnits }}</label>
                 <el-slider v-model="xSlider" :min="100" :max="400" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>代数奥义：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PolynomialViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      xSlider: 200,
      aVal: 60,
      bVal: 40,
      mode: 'expand'
    }
  },
  computed: {
    xSize() { return this.xSlider; },
    aSize() { return this.aVal * (this.xSlider / 100); },
    bSize() { return this.bVal * (this.xSlider / 100); },
    xUnits() { return Math.round(this.xSlider / 2); },
    topicIntro() {
       if (this.mode === 'factor') return '因式分解原理：将多项式面积拆解为矩形边长的乘积';
       return '乘法公式原理：通过几何面积模型直观理解多项式展开的逻辑';
    },
    activeFormula() {
       if (this.mode === 'factor') {
          if (this.aVal === this.bVal) return `x² + ${2*this.aVal}x + ${this.aVal*this.aVal} = (x + a)²`;
          return `x² + ${this.aVal + this.bVal}x + ${this.aVal * this.bVal} = (x + a)(x + b)`;
       }
       if (this.aVal === this.bVal) return '(x + a)² = x² + 2ax + a²';
       return '(x + a)(x + b) = x² + (a + b)x + ab';
    },
    modeDesc() {
       if (this.mode === 'factor') return '因式分解是整式乘法的逆运算。通过面积模型，我们将一个大矩形的面积（多项式）还原为它的长与宽的乘积（因式）';
       return '整式乘法可以视为两个维度长度的乘积。面积模型将复杂的代数展开转化为四块简单矩形的拼接，清晰展示了每一项的几何来源';
    },
    tipText() {
       return '调节 a 和 b 的值，观察当 a = b 时如何演变为完全平方公式。';
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/因式分解|分解|因式/.test(kp)) {
         this.mode = 'factor';
      } else {
         this.mode = 'expand';
      }
    }
  }
}
</script>

<style scoped lang="scss">
.polynomial-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #fdf2f8; color: #9d174d; border: 1px solid #fbcfe8; padding: 5px 16px; border-radius: 20px; font-weight: 900; font-family: "Courier New", monospace; font-size: 14px; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #334155; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 900; } &.blue { border-left: 4px solid #3b82f6; } &.cyan { border-left: 4px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 4px; display: block; } } }
.discovery-card-compact { background: #f8fafc; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #334155; line-height: 1.6; strong { color: #0f172a; font-weight: 900; } } } }
</style>
